import { Mail, Phone, MapPin, Youtube, Facebook, Instagram } from "lucide-react";
import { PARISH } from "@/lib/constants";

const Contact = () => {
    return (
        <div className="mx-auto max-w-7xl px-4 sm:px-6 lg:px-8 py-14">
            <span className="eyebrow">Visit · Call · Write</span>
            <h1 className="heading-display mt-2">Contact the Parish</h1>
            <p className="mt-4 max-w-2xl text-maroon-700/85">
                Our parish office welcomes visitors and inquiries. Reach us by phone, email, or simply stop by, we are located on Othaya Road, Kileleshwa.
            </p>

            <div className="mt-10 grid gap-8 md:grid-cols-2">
                <div className="space-y-5">
                    <div className="group relative overflow-hidden rounded-2xl border border-gold-300/40 bg-gradient-to-br from-maroon-700 to-maroon-900 p-5 text-cream-50 shadow-md ring-1 ring-transparent hover:ring-gold-300 hover:shadow-xl transition-all">
                        <div className="flex items-center gap-3">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold-300/60 bg-maroon-900/70 text-gold-300">
                                <MapPin size={18} />
                            </span>
                            <h3 className="font-serif text-xl text-cream-50">Location</h3>
                        </div>
                        <div className="gold-divider my-3 opacity-60" />
                        <p className="text-cream-50/90">
                            <span className="font-semibold text-cream-50">{PARISH.addressShort}</span><br />
                            {PARISH.address.line2}
                        </p>
                        <a
                            href={PARISH.mapUrl}
                            target="_blank"
                            rel="noreferrer"
                            className="mt-3 inline-flex items-center gap-1 text-xs font-semibold uppercase tracking-wider text-gold-300 hover:text-gold-200"
                        >
                            Get directions →
                        </a>
                    </div>

                    <div className="group relative overflow-hidden rounded-2xl border border-gold-300/40 bg-gradient-to-br from-maroon-700 to-maroon-900 p-5 text-cream-50 shadow-md ring-1 ring-transparent hover:ring-gold-300 hover:shadow-xl transition-all">
                        <div className="flex items-center gap-3">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold-300/60 bg-maroon-900/70 text-gold-300">
                                <Phone size={18} />
                            </span>
                            <h3 className="font-serif text-xl text-cream-50">Phone</h3>
                        </div>
                        <div className="gold-divider my-3 opacity-60" />
                        <a href={`tel:${PARISH.phone.replace(/\s/g, "")}`} className="text-cream-50 hover:text-gold-200 text-lg font-semibold tracking-wide">
                            {PARISH.phone}
                        </a>
                    </div>

                    <div className="group relative overflow-hidden rounded-2xl border border-gold-300/40 bg-gradient-to-br from-maroon-700 to-maroon-900 p-5 text-cream-50 shadow-md ring-1 ring-transparent hover:ring-gold-300 hover:shadow-xl transition-all">
                        <div className="flex items-center gap-3">
                            <span className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-gold-300/60 bg-maroon-900/70 text-gold-300">
                                <Mail size={18} />
                            </span>
                            <h3 className="font-serif text-xl text-cream-50">Email</h3>
                        </div>
                        <div className="gold-divider my-3 opacity-60" />
                        <ul className="space-y-1.5 text-cream-50/95">
                            <li>
                                <span className="text-[11px] uppercase tracking-[0.18em] text-gold-300 mr-2">Info</span>
                                <a href={`mailto:${PARISH.emails.info}`} className="hover:text-gold-200">{PARISH.emails.info}</a>
                            </li>
                            <li>
                                <span className="text-[11px] uppercase tracking-[0.18em] text-gold-300 mr-2">Priest</span>
                                <a href={`mailto:${PARISH.emails.priest}`} className="hover:text-gold-200">{PARISH.emails.priest}</a>
                            </li>
                            <li>
                                <span className="text-[11px] uppercase tracking-[0.18em] text-gold-300 mr-2">Catechist</span>
                                <a href={`mailto:${PARISH.emails.catechist}`} className="hover:text-gold-200">{PARISH.emails.catechist}</a>
                            </li>
                        </ul>
                    </div>

                    <div className="group relative overflow-hidden rounded-2xl border border-gold-300/40 bg-gradient-to-br from-maroon-700 to-maroon-900 p-5 text-cream-50 shadow-md ring-1 ring-transparent hover:ring-gold-300 hover:shadow-xl transition-all">
                        <h3 className="font-serif text-xl text-cream-50">Follow Us</h3>
                        <div className="gold-divider my-3 opacity-60" />
                        <div className="grid gap-2">
                            <a href={PARISH.socials.youtube} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-cream-50/95 hover:text-gold-200" data-testid="contact-youtube">
                                <Youtube size={18} className="text-gold-300" /> YouTube, @HolyTrinityCatholicChurchKile
                            </a>
                            <a href={PARISH.socials.facebook} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-cream-50/95 hover:text-gold-200" data-testid="contact-facebook">
                                <Facebook size={18} className="text-gold-300" /> Facebook, @HolyTrinityKileleshwa
                            </a>
                            <a href={PARISH.socials.instagramParish} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-cream-50/95 hover:text-gold-200" data-testid="contact-instagram-parish">
                                <Instagram size={18} className="text-gold-300" /> Instagram, @holytrinitykile
                            </a>
                            <a href={PARISH.socials.instagramYouth} target="_blank" rel="noreferrer" className="flex items-center gap-3 text-cream-50/95 hover:text-gold-200" data-testid="contact-instagram-youth">
                                <Instagram size={18} className="text-gold-300" /> Instagram (Youth · YCA · YSC), @holytrinitykileleshwa_youth
                            </a>
                        </div>
                    </div>
                </div>

                <div className="rounded-2xl border border-gold-300/40 bg-white/80 overflow-hidden">
                    <iframe
                        title="Holy Trinity Kileleshwa map"
                        src={PARISH.mapEmbed}
                        loading="lazy"
                        className="w-full h-[420px] md:h-[640px]"
                        referrerPolicy="no-referrer-when-downgrade"
                    />
                </div>
            </div>
        </div>
    );
};

export default Contact;
